import { useMemo } from 'react'
import {
  queryOptions,
  type Mutation,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
  useMutationState,
  useQueries,
} from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { PAGINATION_ORDER } from 'shared/constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { CommentInterface } from '../components/comment/comment.types'
import type { OnErrorType } from '../types'
import { getCommentSortParams } from '../utils/comment-sorting'
import { commentSourceKeyMap } from '../utils/constants'
import { mergeComments } from '../utils/merge-comments'
import { commentsKeys, type PageCommentsParams } from './key-factory'

const PAGE_COMMENTS_DEFAULT_PAGINATION_LIMIT = 10

const getPageCommentsUrl = ({
  commentsType,
  lastCommentId,
  pageId,
  parentId,
  sortOption,
  targetCommentId,
}: PageCommentsParams): string => {
  const searchParams = new URLSearchParams()
  const commentSourceKey = commentSourceKeyMap[commentsType]
  const apiEndpoint = targetCommentId ? `${COMMENT_API_PREFIX}/focus` : COMMENT_API_PREFIX
  const { sortBy, sortOrder } = getCommentSortParams(sortOption)

  searchParams.set('pagination[limit]', PAGE_COMMENTS_DEFAULT_PAGINATION_LIMIT.toString())
  searchParams.set('pagination[parent][sortOrder]', sortOrder)
  searchParams.set('pagination[children][sortOrder]', PAGINATION_ORDER.ASC)
  searchParams.set('pagination[parent][sortBy]', sortBy)
  searchParams.set('parent', parentId?.toString() ?? 'null')
  searchParams.set(commentSourceKey, pageId.toString())
  if (targetCommentId) searchParams.set('commentId', targetCommentId.toString())
  if (lastCommentId !== 0) searchParams.set('pagination[cursor]', lastCommentId.toString())

  return `${apiEndpoint}?${searchParams.toString()}`
}

interface UsePageCommentsOptions extends Omit<PageCommentsParams, 'lastCommentId'> {
  initialData?: CommentInterface[]
  enabled?: boolean
  onError?: OnErrorType
}

const normalizeReplyResponse = (
  comments: CommentInterface[],
  parentId: number | null,
): CommentInterface[] => {
  if (parentId === null) return comments

  const parent = comments.find(comment => comment.id === parentId)
  return parent === undefined ? comments : (parent.children ?? [])
}

export const usePageComments = ({
  initialData,
  enabled = true,
  onError,
  ...params
}: UsePageCommentsOptions): Pick<
  UseInfiniteQueryResult<CommentInterface[]>,
  'data' | 'fetchNextPage' | 'isError' | 'isFetching' | 'isLoading' | 'refetch'
> & { deletedCommentIds: number[]; resolvedFocusCommentIds: number[] } => {
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, CommentInterface[]>({
    method: RequestMethodsEnum.get,
  })
  const queryMeta = {
    onError,
    sentryContext: params.parentId === null ? 'Comments error' : 'Comments replies error',
  }
  const baseQuery = useInfiniteQuery<
    CommentInterface[],
    Error,
    CommentInterface[],
    ReturnType<typeof commentsKeys.page>,
    number
  >({
    queryKey: commentsKeys.page(params),
    queryFn: async ({ pageParam, signal }) => {
      const data = await fetcher(
        getPageCommentsUrl({
          ...params,
          lastCommentId: pageParam,
          targetCommentId: undefined,
        }),
      )
      signal.throwIfAborted()
      return normalizeReplyResponse(data, params.parentId)
    },
    initialPageParam: 0,
    getNextPageParam: (_lastPage, pages) => {
      let comments: CommentInterface[] = []
      for (const page of pages) comments = mergeComments(comments, page)
      const commentWithMissingNext = comments.find(
        (comment, index) => comment.nextId !== null && comments[index + 1]?.id !== comment.nextId,
      )
      return commentWithMissingNext?.id
    },
    // Defined initial data lets invalidation replace an in-flight first read instead of joining it.
    initialData: { pages: [initialData ?? []], pageParams: [0] },
    select: data => {
      let comments: CommentInterface[] = []
      for (const page of data.pages) comments = mergeComments(comments, page)
      return comments
    },
    // A zero timestamp keeps the seeded page stale until the server response arrives.
    initialDataUpdatedAt: 0,
    ...(params.parentId === null ? {} : { staleTime: 0, refetchOnMount: 'always' as const }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
    meta: queryMeta,
  })
  const baseComments = useMemo(() => {
    if (initialData === undefined || params.parentId === null) return baseQuery.data
    return mergeComments(baseQuery.data, initialData)
  }, [baseQuery.data, initialData, params.parentId])
  const deletedCommentIds = useMutationState<number[], Mutation<void, Error, number>>({
    filters: {
      exact: true,
      mutationKey: commentsKeys.delete(params.commentsType, params.pageId),
      predicate: mutation =>
        mutation.state.status === 'pending' || mutation.state.status === 'success',
    },
    select: mutation => {
      const commentId = mutation.state.variables
      return commentId === undefined ? [] : [commentId]
    },
  }).flat()
  const addedCommentIds = useMutationState<number[], Mutation<CommentInterface>>({
    filters: {
      exact: true,
      mutationKey: commentsKeys.add(params.commentsType, params.pageId),
      status: 'success',
    },
    select: mutation => {
      const comment = mutation.state.data
      if (!comment) return []
      if (Array.isArray(comment.parent)) return params.parentId === null ? [comment.id] : []
      return comment.parent.id === params.parentId ? [comment.id] : []
    },
  }).flat()
  const focusTargetCommentIds = useMemo(() => {
    const targetCommentIds: number[] = []
    const addTarget = (targetCommentId: number | undefined): void => {
      if (
        targetCommentId !== undefined &&
        !deletedCommentIds.includes(targetCommentId) &&
        !targetCommentIds.includes(targetCommentId) &&
        !baseComments.some(
          comment =>
            comment.id === targetCommentId ||
            comment.children?.some(child => child.id === targetCommentId) === true,
        )
      ) {
        targetCommentIds.push(targetCommentId)
      }
    }

    addTarget(params.targetCommentId)
    for (const commentId of addedCommentIds) addTarget(commentId)
    return targetCommentIds
  }, [addedCommentIds, baseComments, deletedCommentIds, params.targetCommentId])
  const {
    comments: focusComments,
    isFetching: isFetchingFocus,
    retryFailedQuery,
    resolvedFocusCommentIds,
  } = useQueries({
    queries: focusTargetCommentIds.map(focusedCommentId =>
      queryOptions({
        queryKey: commentsKeys.focus({ ...params, targetCommentId: focusedCommentId }),
        queryFn: async ({ signal }) => {
          const data = await fetcher(
            getPageCommentsUrl({
              ...params,
              lastCommentId: 0,
              targetCommentId: focusedCommentId,
            }),
          )
          signal.throwIfAborted()
          return normalizeReplyResponse(data, params.parentId)
        },
        select: comments => ({ comments, focusedCommentId }),
        ...(params.parentId === null ? {} : { staleTime: 0, refetchOnMount: 'always' as const }),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled,
        meta: queryMeta,
      }),
    ),
    combine: queries => {
      let comments: CommentInterface[] = []
      const failedQuery = queries.find(query => query.error)
      for (const query of queries) {
        if (query.data) comments = mergeComments(comments, query.data.comments)
      }
      const retryFailedFocusQuery =
        failedQuery === undefined ? undefined : async (): Promise<unknown> => failedQuery.refetch()
      return {
        comments,
        isFetching: queries.some(query => query.isFetching),
        resolvedFocusCommentIds: queries.flatMap(query =>
          query.data ? [query.data.focusedCommentId] : [],
        ),
        retryFailedQuery: retryFailedFocusQuery,
      }
    },
  })
  const data = useMemo(
    () => mergeComments(baseComments, focusComments),
    [baseComments, focusComments],
  )
  const isFetching = baseQuery.isFetching || isFetchingFocus
  const refetch: typeof baseQuery.refetch = async options => {
    if (retryFailedQuery) await retryFailedQuery()
    return baseQuery.refetch(options)
  }

  return {
    data,
    deletedCommentIds,
    resolvedFocusCommentIds,
    fetchNextPage: baseQuery.fetchNextPage,
    isError: baseQuery.isError || retryFailedQuery !== undefined,
    isFetching,
    isLoading: baseQuery.isFetching && baseQuery.dataUpdatedAt === 0,
    refetch,
  }
}
